import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import authService from '../services/authService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './AuthPage.css';
import ppslogo from '../assets/ppslogo.png';

const AuthPage = () => {
  const [activePanel, setActivePanel] = useState('login');
  const [greeting, setGreeting] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const passwordRequirementsRef = useRef(null);
  const triggerRef = useRef(null);

  // Form states
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: ''
  });

  const [forgotForm, setForgotForm] = useState({
    username: '',
    email: ''
  });

  const [requestForm, setRequestForm] = useState({
    name: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    reasonForAccess: ''
  });

  const [passwordErrors, setPasswordErrors] = useState([]);
  const [passwordValidations, setPasswordValidations] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
    special: false,
    match: false
  });

  useEffect(() => {
    if (location.pathname === '/forgot-password') {
      setActivePanel('forgot');
    } else if (location.pathname === '/request-access') {
      setActivePanel('request');
    } else {
      setActivePanel('login');
    }

    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Good Morning');
    else if (hour < 18) setGreeting('Good Afternoon');
    else setGreeting('Good Evening');
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
          showPasswordRequirements &&
          passwordRequirementsRef.current &&
          !passwordRequirementsRef.current.contains(event.target) &&
          triggerRef.current &&
          !triggerRef.current.contains(event.target)
      ) {
        setShowPasswordRequirements(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPasswordRequirements]);

  const handleInputChange = (e, formType) => {
    const { name, value } = e.target;
    switch(formType) {
      case 'login':
        setLoginForm(prev => ({...prev, [name]: value}));
        break;
      case 'forgot':
        setForgotForm(prev => ({...prev, [name]: value}));
        break;
      case 'request':
        const updatedForm = {...requestForm, [name]: value};
        setRequestForm(updatedForm);

        // Validate password when password fields change
        if (name === 'password' || name === 'confirmPassword') {
          validatePassword(updatedForm.password, updatedForm.confirmPassword);
        }
        break;
      default:
        break;
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const validatePhone = (phone) => {
    const re = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;
    return re.test(phone);
  };

  const validatePassword = (password, confirmPassword) => {
    const errors = [];
    const validations = {
      length: password.length >= 8,
      lowercase: /(?=.*[a-z])/.test(password),
      uppercase: /(?=.*[A-Z])/.test(password),
      number: /(?=.*\d)/.test(password),
      special: /(?=.*[@$!%*?&])/.test(password),
      match: password === confirmPassword && password !== ''
    };

    setPasswordValidations(validations);

    if (!validations.length) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!validations.lowercase) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!validations.uppercase) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!validations.number) {
      errors.push('Password must contain at least one number');
    }
    if (!validations.special) {
      errors.push('Password must contain at least one special character (@$!%*?&)');
    }
    if (!validations.match) {
      errors.push('Passwords do not match');
    }

    setPasswordErrors(errors);
    return errors.length === 0;
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!loginForm.username || !loginForm.password) {
      toast.error('Please fill in all fields', {
        position: "top-center",
        className: 'toast-error'
      });
      return;
    }

    try {
      setIsLoading(true);
      const response = await login(loginForm);

      toast.success(response.message || 'Login successful!', {
        position: "top-center",
        className: 'toast-success',
        autoClose: 2000,
        onClose: () => navigate('/dashboard')
      });

    } catch (err) {
      toast.error(err.message || 'Login failed. Please check your credentials and try again.', {
        position: "top-center",
        className: 'toast-error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    setActivePanel('forgot');
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotForm.username || !forgotForm.email) {
      toast.error('Please enter both username and email address', {
        position: "top-center",
        className: 'toast-error'
      });
      return;
    }

    if (!validateEmail(forgotForm.email)) {
      toast.error('Please enter a valid email address', {
        position: "top-center",
        className: 'toast-error'
      });
      return;
    }

    try {
      setIsLoading(true);
      await authService.forgotPassword(forgotForm);
      toast.success('Password reset link has been sent to your email!', {
        position: "top-center",
        className: 'toast-success',
        autoClose: 3000,
        onClose: () => {
          setActivePanel('login');
        }
      });
    } catch (err) {
      toast.error(err.message || 'Failed to send reset link. Please try again.', {
        position: "top-center",
        className: 'toast-error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestAccess = (e) => {
    e.preventDefault();
    setActivePanel('request');
  };

  const handleBackToLogin = (e) => {
    e.preventDefault();
    setActivePanel('login');
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();

    // Validate all required fields
    if (!requestForm.name || !requestForm.email || !requestForm.reasonForAccess ||
        !requestForm.username || !requestForm.password || !requestForm.confirmPassword) {
      toast.error('Please fill in all required fields', {
        position: "top-center",
        className: 'toast-error'
      });
      return;
    }

    if (!validateEmail(requestForm.email)) {
      toast.error('Please enter a valid email address', {
        position: "top-center",
        className: 'toast-error'
      });
      return;
    }

    // Validate password
    const isPasswordValid = validatePassword(requestForm.password, requestForm.confirmPassword);
    if (!isPasswordValid) {
      toast.error('Please fix password errors before submitting', {
        position: "top-center",
        className: 'toast-error'
      });
      return;
    }

    try {

      setIsLoading(true);

      const response = await authService.requestAccess(requestForm);

      console.log('response::::::::::'+response);

      toast.success(response || 'Access request submitted successfully! We will contact you shortly.', {
        position: "top-center",
        className: 'toast-success',
        autoClose: 3000,
        onClose: () => {
          setActivePanel('login');
        }
      });

      console.log('after response and toast::::::::::'+response);

    } catch (err) {
      const errorMessage = err.response?.data?.message ||
          err.message ||
          'Failed to submit request. Please try again.';
      toast.error(errorMessage, {
        position: "top-center",
        className: 'toast-error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordRequirements = () => {
    setShowPasswordRequirements(!showPasswordRequirements);
  };

  return (
      <div className="auth-wrapper">
        <ToastContainer
            position="top-center"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
        />
        <div className={`auth-container ${activePanel}-active`}>
          {/* Login Form */}
          <div className={`form-container login-container ${activePanel === 'login' ? 'active' : ''}`}>
            <div className="form-header">
              <img src={ppslogo} alt="PPS Logo" className="pps-logo" />
              <h3>PPSLink Authentication</h3>
            </div>
            <form onSubmit={handleLoginSubmit}>
              <div className="input-group">
                <input
                    type="text"
                    name="username"
                    placeholder="Enter your Username"
                    value={loginForm.username}
                    onChange={(e) => handleInputChange(e, 'login')}
                    required
                    disabled={isLoading}
                />
              </div>
              <div className="input-group">
                <input
                    type="password"
                    name="password"
                    placeholder="Enter your password"
                    value={loginForm.password}
                    onChange={(e) => handleInputChange(e, 'login')}
                    required
                    disabled={isLoading}
                />
                <a href="#" onClick={handleForgotPassword} className="forgot-password-link">
                  Forgot password?
                </a>
              </div>
              <button type="submit" disabled={isLoading} className="auth-button">
                {isLoading ? (
                    <>
                      <span className="spinner"></span> PROCESSING...
                    </>
                ) : (
                    'SIGN IN'
                )}
              </button>
              <div className="request-access-link">
                Don't have an account?{' '}
                <a href="#" onClick={handleRequestAccess} className="request-access-link">
                  Request Access
                </a>
              </div>
            </form>
          </div>

          {/* Forgot Password Form */}
          <div className={`form-container forgot-container ${activePanel === 'forgot' ? 'active' : ''}`}>
            <div className="form-header">
              <img src={ppslogo} alt="PPS Logo" className="pps-logo" />
              <h3>Reset Password</h3>
            </div>
            <form onSubmit={handleForgotSubmit}>
              <div className="input-group">
                <input
                    type="text"
                    name="username"
                    placeholder="Enter your username"
                    value={forgotForm.username}
                    onChange={(e) => handleInputChange(e, 'forgot')}
                    required
                    disabled={isLoading}
                />
              </div>
              <div className="input-group">
                <input
                    type="email"
                    name="email"
                    placeholder="Enter your registered email"
                    value={forgotForm.email}
                    onChange={(e) => handleInputChange(e, 'forgot')}
                    required
                    disabled={isLoading}
                />
              </div>
              <button type="submit" disabled={isLoading} className="auth-button">
                {isLoading ? (
                    <>
                      <span className="spinner"></span> SENDING...
                    </>
                ) : (
                    'SEND RESET LINK'
                )}
              </button>
              <a className="back-link" href="#" onClick={handleBackToLogin}>
                Back to Sign In
              </a>
            </form>
          </div>

          {/* Request Access Form */}
          <div className={`form-container request-container ${activePanel === 'request' ? 'active' : ''}`}>
            <div className="form-header">
              <img src={ppslogo} alt="PPS Logo" className="pps-logo" />
              <h3>Request Access</h3>
            </div>
            <form onSubmit={handleRequestSubmit}>
              <div className="input-group">
                <input
                    type="text"
                    name="name"
                    placeholder="Your Full Name"
                    value={requestForm.name}
                    onChange={(e) => handleInputChange(e, 'request')}
                    required
                    disabled={isLoading}
                />
              </div>
              <div className="input-group">
                <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={requestForm.email}
                    onChange={(e) => handleInputChange(e, 'request')}
                    required
                    disabled={isLoading}
                />
              </div>
              <div className="input-group">
                <input
                    type="text"
                    name="username"
                    placeholder="Proposed Username"
                    value={requestForm.username}
                    onChange={(e) => handleInputChange(e, 'request')}
                    required
                    disabled={isLoading}
                />
              </div>
              <div className="input-group">
                <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number (Optional)"
                    value={requestForm.phone}
                    onChange={(e) => handleInputChange(e, 'request')}
                    disabled={isLoading}
                />
              </div>
              <div className="input-group">
                <input
                    type="password"
                    name="password"
                    placeholder="Create Password"
                    value={requestForm.password}
                    onChange={(e) => handleInputChange(e, 'request')}
                    required
                    disabled={isLoading}
                />
              </div>
              <div className="input-group password-requirements-wrapper">
                <input
                    type="password"
                    name="confirmPassword"
                    placeholder="Confirm Password"
                    value={requestForm.confirmPassword}
                    onChange={(e) => handleInputChange(e, 'request')}
                    required
                    disabled={isLoading}
                />
                <div
                    ref={triggerRef}
                    className="password-requirements-trigger"
                    onClick={togglePasswordRequirements}
                >
                  View Password Requirements <span>▼</span>
                </div>

                <div
                    ref={passwordRequirementsRef}
                    className={`password-requirements ${showPasswordRequirements ? 'show' : ''}`}
                >
                  <h4>Password Requirements:</h4>
                  <ul>
                    <li className={passwordValidations.length ? 'valid' : 'password-error'}>
                      At least 8 characters long
                    </li>
                    <li className={passwordValidations.lowercase ? 'valid' : 'password-error'}>
                      At least one lowercase letter
                    </li>
                    <li className={passwordValidations.uppercase ? 'valid' : 'password-error'}>
                      At least one uppercase letter
                    </li>
                    <li className={passwordValidations.number ? 'valid' : 'password-error'}>
                      At least one number
                    </li>
                    <li className={passwordValidations.special ? 'valid' : 'password-error'}>
                      At least one special character (@$!%*?&)
                    </li>
                    <li className={passwordValidations.match ? 'valid' : 'password-error'}>
                      Passwords match
                    </li>
                  </ul>
                </div>
              </div>

              <div className="input-group">
                <textarea
                    name="reasonForAccess"
                    placeholder="Reason for access request"
                    value={requestForm.reasonForAccess}
                    onChange={(e) => handleInputChange(e, 'request')}
                    required
                    disabled={isLoading}
                    rows="4"
                />
              </div>
              <button type="submit" disabled={isLoading} className="auth-button">
                {isLoading ? (
                    <>
                      <span className="spinner"></span> SUBMITTING...
                    </>
                ) : (
                    'SUBMIT REQUEST'
                )}
              </button>
              <a className="back-link" href="#" onClick={handleBackToLogin}>
                Back to Sign In
              </a>
            </form>
          </div>

          {/* Info Panel */}
          <div className="info-panel">
            <h1>{greeting}</h1>
            <div className="middleware-features">
              <div className="feature-divider"></div>
              <h2>Integrations Middleware</h2>
            </div>
          </div>
        </div>
      </div>
  );
};

export default AuthPage;